import { useEffect, useMemo, useRef, useState } from 'react'

import { logger } from '~/debug/logger'
import { usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { SynopLinkModel } from '../client/ders'
import { useGetSynoplinksQuery } from '../client/ders'
import { combineSkip } from '../hooks/utils'
import type { IndexedDbService } from './storage/IndexedDbService'
import type { SynopLinkCacheRecord } from './storage/schema'

const SYNOPLINKS_PAGE_SIZE = 1000

export type SynopLinksDataManagerContextValue = {
  data: SynopLinkModel[]
  isLoading: boolean
  isFetching: boolean
}

function useSynopLinksQuery(options?: { skip?: boolean }) {
  const principalOrgId = usePrincipalOrgId()

  const { data, isLoading, isFetching } = useGetSynoplinksQuery(
    {
      orgId: principalOrgId ? [principalOrgId] : undefined,
      includeChildren: true,
      page: 0,
      count: SYNOPLINKS_PAGE_SIZE
    },
    combineSkip(!principalOrgId, options)
  )

  return {
    synoplinks: data?.data,
    isLoading,
    isFetching
  }
}

export default function useSynopLinksDataManager(service: IndexedDbService) {
  const { synoplinks, isLoading, isFetching } = useSynopLinksQuery()

  const inMemorySynopLinks = useSynopLinksPersistence(service, synoplinks)

  return useMemo<SynopLinksDataManagerContextValue>(
    () => ({
      data: inMemorySynopLinks,
      isLoading,
      isFetching
    }),
    [inMemorySynopLinks, isLoading, isFetching]
  )
}

function useSynopLinksPersistence(service: IndexedDbService, synoplinks?: SynopLinkModel[]) {
  const [inMemorySynopLinks, setInMemorySynopLinks] = useState<SynopLinkModel[]>([])
  const receivedApiDataRef = useRef(false)

  useEffect(() => {
    async function hydrateSynopLinksCache() {
      try {
        const records = await service.getAll('synoplinks')
        if (!receivedApiDataRef.current) {
          setInMemorySynopLinks(records.map((record) => record.payload))
        }
      } catch (error) {
        logger.error('Failed to hydrate synoplinks cache', error)
      }
    }

    void hydrateSynopLinksCache()
  }, [service])

  useEffect(() => {
    if (!synoplinks) return

    receivedApiDataRef.current = true
    setInMemorySynopLinks(synoplinks)

    const fetchedAt = Date.now()
    const records: SynopLinkCacheRecord[] = synoplinks.map((synoplink) => ({
      id: synoplink.id,
      payload: synoplink,
      syncedAt: fetchedAt
    }))

    const replaceRecords = async () => {
      try {
        await service.replace('synoplinks', records)
      } catch (error) {
        logger.error('Failed to persist synoplinks snapshot', error)
      }
    }

    void replaceRecords()
  }, [synoplinks, service])

  return inMemorySynopLinks
}
